import { useState, useEffect, useRef } from "react";
import SmartImage from "@/components/SmartImage";
import { cardImage } from "@/lib/img";
import { Link, useNavigate } from "react-router-dom";
import { Search, Menu, X, User, LogOut, LayoutDashboard, Tag, Star, BookOpen, Ticket, ChevronDown, Plane, HeartPulse, Heart, Home, Sparkles, Settings } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useFavorites } from "@/context/FavoritesContext";
import { Button } from "@/components/ui/button";
import { Avatar } from "@/components/Avatar";
import { categoryImage } from "@/lib/categoryImages";
import api from "@/lib/api";

const NAV = [
  { label: "Home", to: "/", icon: Home },
  { label: "Deals", to: "/deals", icon: Tag },
  { label: "Travel", to: "/travel", icon: Plane },
  { label: "Health", to: "/health", icon: HeartPulse },
  { label: "Coupons", to: "/coupons", icon: Ticket },
  { label: "Reviews", to: "/reviews", icon: Star },
  { label: "Guides", to: "/guides", icon: BookOpen },
];

const TYPE_TAG = { deal: "Deal", review: "Review", coupon: "Coupon", guide: "Guide" };

export default function Navbar() {
  const { user, logout } = useAuth();
  const { items, dealItems } = useFavorites();
  const savedCount = items.length + dealItems.length;
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [reviewCats, setReviewCats] = useState([]);
  const [revOpen, setRevOpen] = useState(false);
  const [featured, setFeatured] = useState(null);
  const catRef = useRef(null);
  const revRef = useRef(null);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/reviews/categories").then((r) => setReviewCats(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    api.get("/reviews/featured-roundup").then((r) => setFeatured(r.data && r.data.slug ? r.data : null)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!revOpen) return;
    const onDown = (e) => { if (revRef.current && !revRef.current.contains(e.target)) setRevOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [revOpen]);

  useEffect(() => {
    if (!catOpen) return;
    const onDown = (e) => { if (catRef.current && !catRef.current.contains(e.target)) setCatOpen(false); };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [catOpen]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/search/suggest?q=${encodeURIComponent(term)}`);
        setSuggestions(Array.isArray(data) ? data : []);
      } catch { setSuggestions([]); }
    }, 200);
    return () => clearTimeout(t);
  }, [q]);

  const submit = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
    setOpen(false);
    setFocused(false);
  };

  const choose = (s) => {
    setFocused(false);
    setSuggestions([]);
    if (s.to) { navigate(s.to); setQ(""); }
    else { setQ(s.label); navigate(`/search?q=${encodeURIComponent(s.label)}`); }
  };

  const showSuggest = focused && suggestions.length > 0;

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-ink-950/95 border-b border-white/10 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center gap-4 h-16">
          <Link to="/" className="flex items-center shrink-0" data-testid="navbar-logo">
            <img src="/gkdeals-logo-sm.webp" alt="gkdeals.com" width="71" height="40" className="h-10 w-auto" />
          </Link>

          <nav className="hidden lg:flex flex-1 items-center justify-between ml-8 mr-6">
            {categories.length > 0 && (
              <div className="relative" ref={catRef}>
                <button data-testid="nav-categories-btn" onClick={() => setCatOpen((v) => !v)} onMouseEnter={() => setCatOpen(true)}
                  className="px-3 py-2 text-sm font-semibold text-white/80 hover:text-brand transition-colors rounded-md inline-flex items-center gap-1">
                  Shop by Category <ChevronDown className={`w-3.5 h-3.5 transition-transform ${catOpen ? "rotate-180" : ""}`} />
                </button>
                {catOpen && (
                  <div data-testid="nav-categories-menu" className="absolute left-0 mt-2 w-[min(560px,calc(100vw-2rem))] bg-white rounded-2xl border border-neutral-200 shadow-[0_16px_48px_rgba(0,0,0,0.16)] p-4 z-50">
                    <div className="flex items-center justify-between px-1 pb-3 mb-1">
                      <span className="text-xs tracking-[0.18em] uppercase font-bold text-neutral-400">Shop by Category</span>
                      <Link to="/deals" onClick={() => setCatOpen(false)} className="text-xs font-semibold text-brand hover:gap-1.5 inline-flex items-center gap-1 transition-all">
                        All deals <ChevronDown className="w-3 h-3 -rotate-90" />
                      </Link>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                      {categories.map((c) => (
                        <Link key={c.id} to={`/category/${c.slug}`} data-testid={`nav-cat-${c.slug}`} onClick={() => setCatOpen(false)}
                          className="group/cat flex items-center gap-3 rounded-xl border border-neutral-200 hover:border-brand hover:shadow-sm p-2 transition-[border-color,box-shadow]">
                          <span className="w-14 h-14 rounded-lg overflow-hidden bg-neutral-100 shrink-0">
                            <img src={categoryImage(c.slug, 120)} alt={c.name} loading="lazy"
                              className="w-full h-full object-cover group-hover/cat:scale-105 transition-transform duration-300" />
                          </span>
                          <span className="min-w-0">
                            <span className="block font-display font-bold text-sm text-ink-900 group-hover/cat:text-brand transition-colors">{c.name}</span>
                            <span className="block text-xs text-neutral-400">Deals · Guides · Reviews</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                    {featured && (
                      <Link to={`/reviews/${featured.slug}`} data-testid="nav-featured-roundup" onClick={() => setCatOpen(false)}
                        className="group/feat mt-3 flex items-center gap-3 rounded-xl bg-ink-950 text-white p-2.5 hover:bg-ink-900 transition-colors">
                        <span className="w-14 h-14 rounded-lg overflow-hidden bg-white/10 shrink-0">
                          {featured.image_url
                            ? <SmartImage width={480} src={featured.image_url} alt={featured.title} loading="lazy" className="w-full h-full object-cover group-hover/feat:scale-105 transition-transform duration-300" />
                            : <span className="w-full h-full flex items-center justify-center"><Star className="w-5 h-5 text-brand" /></span>}
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase font-bold text-[#FFE14D] mb-0.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> New Comparison Guide
                          </span>
                          <span className="block font-display font-bold text-sm leading-tight truncate">{featured.title}</span>
                          {featured.item_count > 0 && <span className="block text-xs text-white/50">{featured.item_count} products ranked & compared</span>}
                        </span>
                        <ChevronDown className="w-4 h-4 -rotate-90 text-white/60 group-hover/feat:text-white shrink-0" />
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}
            {NAV.map((n) => (
              n.label === "Reviews" && reviewCats.length > 0 ? (
                <div key={n.to} className="relative" ref={revRef}>
                  <button type="button" data-testid="nav-reviews" onClick={() => setRevOpen((v) => !v)} onMouseEnter={() => setRevOpen(true)}
                    className="px-3 py-2 text-sm font-semibold text-white/80 hover:text-brand transition-colors rounded-md inline-flex items-center gap-1">
                    Reviews <ChevronDown className={`w-3.5 h-3.5 transition-transform ${revOpen ? "rotate-180" : ""}`} />
                  </button>
                  {revOpen && (
                    <div data-testid="nav-reviews-menu" className="absolute left-0 mt-2 w-[min(620px,calc(100vw-2rem))] bg-white rounded-2xl border border-neutral-200 shadow-[0_16px_48px_rgba(0,0,0,0.16)] p-4 z-50">
                      <div className="flex items-center justify-between px-1 pb-3 mb-1">
                        <span className="text-xs tracking-[0.18em] uppercase font-bold text-neutral-400">Reviews by Category</span>
                        <Link to="/reviews" onClick={() => setRevOpen(false)} className="text-xs font-semibold text-brand hover:gap-1.5 inline-flex items-center gap-1 transition-all">
                          All reviews <ChevronDown className="w-3 h-3 -rotate-90" />
                        </Link>
                      </div>
                      <div className="grid grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto overscroll-contain">
                        {reviewCats.map((c) => (
                          <Link key={c.name} to={`/reviews/${c.slug}`} data-testid={`nav-review-cat-${c.name}`} onClick={() => setRevOpen(false)}
                            className="group/rev flex items-center gap-3 rounded-xl border border-neutral-200 hover:border-brand hover:shadow-sm px-3 py-2.5 transition-[border-color,box-shadow]">
                            <span className="w-9 h-9 rounded-lg bg-brand/5 text-brand flex items-center justify-center shrink-0 group-hover/rev:bg-brand group-hover/rev:text-white transition-colors">
                              <Star className="w-4 h-4" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block font-display font-bold text-sm text-ink-900 group-hover/rev:text-brand transition-colors truncate">{c.name}</span>
                              <span className="block text-xs text-neutral-400">{c.count} review{c.count === 1 ? "" : "s"}</span>
                            </span>
                          </Link>
                        ))}
                      </div>
                      {featured && (
                        <Link to={`/reviews/${featured.slug}`} data-testid="nav-reviews-featured" onClick={() => setRevOpen(false)}
                          className="group/feat mt-3 flex items-center gap-3 rounded-xl bg-ink-950 text-white p-2.5 hover:bg-ink-900 transition-colors">
                          <span className="w-14 h-14 rounded-lg overflow-hidden bg-white/10 shrink-0">
                            {featured.image_url
                              ? <SmartImage width={480} src={featured.image_url} alt={featured.title} loading="lazy" className="w-full h-full object-cover group-hover/feat:scale-105 transition-transform duration-300" />
                              : <span className="w-full h-full flex items-center justify-center"><Star className="w-5 h-5 text-brand" /></span>}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.15em] uppercase font-bold text-[#FFE14D] mb-0.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Featured Comparison
                            </span>
                            <span className="block font-display font-bold text-sm leading-tight truncate">{featured.title}</span>
                            {featured.item_count > 0 && <span className="block text-xs text-white/50">{featured.item_count} products ranked & compared</span>}
                          </span>
                          <ChevronDown className="w-4 h-4 -rotate-90 text-white/60 group-hover/feat:text-white shrink-0" />
                        </Link>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase().replace(/\s|-/g, "")}`}
                  className="px-3 py-2 text-sm font-semibold text-white/80 hover:text-brand transition-colors rounded-md">
                  {n.label}
                </Link>
              )
            ))}
            {user && (
              <Link to="/saved" data-testid="nav-saved" title="Saved deals & trips"
                className="px-3 py-2 text-sm font-semibold text-white/80 hover:text-brand transition-colors rounded-md inline-flex items-center gap-1.5">
                <span className="relative">
                  <Heart className="w-4 h-4" />
                  {savedCount > 0 && (
                    <span data-testid="nav-saved-count" className="absolute -top-2 -right-2.5 min-w-[16px] h-4 px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center leading-none">{savedCount > 99 ? "99+" : savedCount}</span>
                  )}
                </span>
                Saved
              </Link>
            )}
          </nav>

          <div className="hidden lg:flex items-center gap-2 ml-auto">
            {user ? (
              <div className="relative">
                <button data-testid="navbar-user-btn" onClick={() => setMenu(!menu)}
                  className="flex items-center gap-2 pl-1 pr-2.5 py-1 rounded-full border border-white/25 text-white text-sm font-semibold hover:border-brand transition-colors">
                  <Avatar user={user} size={28} />
                  <span className="max-w-[100px] truncate">{user.name?.split(" ")[0] || "Account"}</span>
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${menu ? "rotate-180" : ""}`} />
                </button>
                {menu && (
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl border border-neutral-200 shadow-[0_12px_40px_rgba(0,0,0,0.14)] py-2 z-50" onMouseLeave={() => setMenu(false)}>
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-neutral-100 mb-1">
                      <Avatar user={user} size={42} textClass="text-base" />
                      <div className="min-w-0">
                        <p className="font-display font-bold text-sm text-ink-900 truncate">{user.name || "Account"}</p>
                        <p className="text-xs text-neutral-500 truncate" data-testid="navbar-user-email">{user.email}</p>
                      </div>
                    </div>
                    <Link to="/account" data-testid="navbar-profile-link" onClick={() => setMenu(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink-800 hover:bg-neutral-50 transition-colors">
                      <Settings className="w-4 h-4 text-neutral-400" /> My Profile
                    </Link>
                    <Link to="/saved" onClick={() => setMenu(false)}
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink-800 hover:bg-neutral-50 transition-colors">
                      <Heart className="w-4 h-4 text-neutral-400" /> Saved
                      {savedCount > 0 && <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center">{savedCount > 99 ? "99+" : savedCount}</span>}
                    </Link>
                    {user.role === "admin" && (
                      <Link to="/admin" data-testid="navbar-admin-link" onClick={() => setMenu(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-ink-800 hover:bg-neutral-50 transition-colors">
                        <LayoutDashboard className="w-4 h-4 text-neutral-400" /> Admin Dashboard
                      </Link>
                    )}
                    <div className="border-t border-neutral-100 mt-1 pt-1">
                      <button data-testid="navbar-logout-btn" onClick={() => { logout(); setMenu(false); navigate("/"); }}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-sm hover:bg-neutral-50 w-full text-left text-brand font-semibold transition-colors">
                        <LogOut className="w-4 h-4" /> Log out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login"><Button data-testid="navbar-login-btn" className="rounded-full font-semibold bg-brand hover:bg-brand-hover px-6">Login</Button></Link>
            )}
          </div>

          <button data-testid="navbar-mobile-toggle" className="lg:hidden ml-auto p-2 text-white" onClick={() => setOpen(!open)}>
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Full-width smart search row (desktop) */}
        <div className="hidden md:block pb-3 -mt-1">
          <form onSubmit={submit} className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                data-testid="navbar-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setTimeout(() => setFocused(false), 150)}
                onKeyDown={(e) => { if (e.key === "Escape") setFocused(false); }}
                placeholder="Try “gold watch under $500” or “Tory Burch bags on sale” — smart search understands you"
                className="w-full pl-11 pr-4 h-12 rounded-full border border-neutral-200 bg-neutral-50 text-sm text-ink-950 placeholder:text-neutral-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus:bg-white transition-colors"
              />
              {showSuggest && (
                <div data-testid="search-suggestions" className="absolute left-0 right-0 top-full mt-2 bg-white rounded-2xl border border-neutral-200 shadow-[0_12px_40px_rgba(0,0,0,0.14)] py-2 overflow-hidden z-50">
                  {suggestions.map((s, i) => (
                    <button type="button" key={i} data-testid={`search-suggestion-${i}`}
                      onMouseDown={(e) => { e.preventDefault(); choose(s); }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-neutral-50 text-left transition-colors">
                      {s.image
                        ? <SmartImage width={160} src={s.image} alt="" className="w-9 h-9 rounded-lg object-cover shrink-0 bg-neutral-100" />
                        : <span className="w-9 h-9 rounded-lg bg-brand/10 text-brand flex items-center justify-center shrink-0"><Search className="w-4 h-4" /></span>}
                      <span className="flex-1 min-w-0 text-sm text-ink-900 truncate">{s.label}</span>
                      <span className="shrink-0 text-[10px] tracking-wider uppercase font-bold text-neutral-400">{TYPE_TAG[s.type] || ""}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" data-testid="navbar-search-btn"
              className="shrink-0 inline-flex items-center gap-1.5 h-12 px-7 rounded-full bg-brand hover:bg-brand-hover text-white text-sm font-semibold transition-colors">
              <Sparkles className="w-4 h-4" /> Smart Search
            </button>
          </form>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-neutral-200 bg-white px-4 py-4 space-y-2">
          <form onSubmit={submit} className="flex items-center gap-2 mb-3">
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="mobile-search-input"
                placeholder="Search deals, reviews..." className="w-full pl-10 pr-4 h-11 rounded-full border border-neutral-200 bg-neutral-50 text-sm text-ink-950 placeholder:text-neutral-400 focus:outline-none" />
            </div>
            <button type="submit" data-testid="mobile-search-btn"
              className="shrink-0 inline-flex items-center justify-center h-11 px-5 rounded-full bg-brand hover:bg-brand-hover text-white text-sm font-semibold">
              <Search className="w-4 h-4" />
            </button>
          </form>
          {NAV.map((n) => (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)}
              className="flex items-center gap-2 py-2.5 font-semibold text-ink-800">
              <n.icon className="w-4 h-4 text-brand" /> {n.label}
            </Link>
          ))}
          {categories.length > 0 && (
            <div className="pt-2 border-t border-neutral-200" data-testid="mobile-categories">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-neutral-400 py-2">Shop by Category</p>
              <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                  <Link key={c.id} to={`/category/${c.slug}`} onClick={() => setOpen(false)} data-testid={`mobile-cat-${c.slug}`}
                    className="px-3 py-1.5 rounded-full bg-brand/5 text-brand border border-brand/20 text-sm font-semibold">
                    {c.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
          {reviewCats.length > 0 && (
            <div className="pt-2 border-t border-neutral-200" data-testid="mobile-review-cats">
              <p className="text-xs tracking-[0.15em] uppercase font-bold text-neutral-400 py-2">Reviews by Category</p>
              <div className="flex flex-wrap gap-2">
                {reviewCats.map((c) => (
                  <Link key={c.name} to={`/reviews/${c.slug}`} onClick={() => setOpen(false)} data-testid={`mobile-review-cat-${c.name}`}
                    className="px-3 py-1.5 rounded-full bg-neutral-100 text-ink-700 border border-neutral-200 text-sm font-semibold">
                    {c.name} <span className="text-neutral-400">{c.count}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}
          {user && (
            <Link to="/account" onClick={() => setOpen(false)} data-testid="nav-profile-mobile"
              className="flex items-center gap-2 py-2.5 font-semibold text-ink-800">
              <Settings className="w-4 h-4 text-brand" /> My Profile
            </Link>
          )}
          {user && (
            <Link to="/saved" onClick={() => setOpen(false)} data-testid="nav-saved-mobile"
              className="flex items-center gap-2 py-2.5 font-semibold text-ink-800">
              <Heart className="w-4 h-4 text-brand" /> Saved
              {savedCount > 0 && <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center">{savedCount > 99 ? "99+" : savedCount}</span>}
            </Link>
          )}
          <div className="pt-2 border-t border-neutral-200 flex gap-2">
            {user ? (
              <>
                {user.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="flex-1"><Button variant="outline" className="w-full rounded-full">Admin</Button></Link>}
                <Button onClick={() => { logout(); setOpen(false); navigate("/"); }} className="flex-1 rounded-full bg-brand hover:bg-brand-hover">Log out</Button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1"><Button className="w-full rounded-full bg-brand hover:bg-brand-hover">Login</Button></Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
